/**
 * MobileSubscriptionPlans Component
 * Mobile page to pick and manage subscription plans (free, pro, ultra)
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './MobileSubscriptionPlans.css';
import useApi from '../../../hooks/useApi';
import useLanguage from '../../../hooks/useLanguage';
import { useMobileAlert } from '../common/alerts/MobileAlertContext';
import { useMobileSwipeBackContext } from '../MobileSwipeBackContext';
import { Skeleton } from '../../effects';
import { trackEvent } from '../../../utils/analytics';

import iconBilling from '../../../assets/images/icons/icon-billing.svg';
import iconAi from '../../../assets/images/icons/icon-ai.svg';

const PLAN_CONFIG = [
  {
    id: 'free',
    name: 'Free',
    badge: 'Incluso',
    className: 'plan-free',
    description: 'Per iniziare a usare Loopino senza pensieri.',
    prices: {
      monthly: { label: '0€', code: null }
    },
    features: [
      'Batteria AI standard',
      'Automazioni base',
      'Accesso a tutti i post approvati'
    ]
  },
  {
    id: 'pro',
    name: 'Pro',
    badge: 'Consigliato',
    className: 'plan-pro',
    highlight: true,
    description: 'Ideale per pubblicare con continuita e avere piu batteria.',
    prices: {
      monthly: { amount: 29, code: 'pro_monthly' },
      yearly: { amount: 290, code: 'pro_yearly', note: 'Risparmia 2 mesi' }
    },
    features: [
      'Batteria AI potenziata',
      'Strategie e review avanzate',
      'Supporto prioritario'
    ]
  },
  {
    id: 'ultra',
    name: 'Ultra',
    badge: 'Per team',
    className: 'plan-ultra',
    description: 'Per chi vuole la massima capacita e strumenti premium.',
    prices: {
      monthly: { amount: 99, code: 'ultra_monthly' },
      yearly: { amount: 990, code: 'ultra_yearly', note: 'Massimo risparmio' }
    },
    features: [
      'Batteria AI moltiplicata',
      'Priorita assoluta nelle generazioni',
      'Assistenza dedicata'
    ]
  }
];

const STATUS_LABELS = {
  active: 'Attivo',
  trialing: 'In prova',
  grace: 'In uso',
  past_due: 'Pagamento in sospeso',
  unpaid: 'Pagamento in sospeso',
  canceled: 'Cancellato',
  incomplete: 'In attesa di pagamento',
  incomplete_expired: 'Pagamento scaduto',
  paused: 'In pausa',
  inactive: 'Nessun abbonamento'
};

const CheckIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M16.6667 5.8335L8.33335 14.1668L3.33335 9.16683" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const MobileSubscriptionPlans = ({ isActive = true }) => {
  const { t, language } = useLanguage();
  const { notify } = useMobileAlert();
  const { get, post } = useApi();
  const { registerSwipeBackGuard } = useMobileSwipeBackContext() || {};

  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [subscription, setSubscription] = useState(null);
  const [isStatusLoading, setIsStatusLoading] = useState(false);
  const [processingAction, setProcessingAction] = useState(null);
  const [isPortalLoading, setIsPortalLoading] = useState(false);

  const billingAlignedRef = useRef(false);

  const currencyFormatter = useMemo(() => {
    try {
      return new Intl.NumberFormat(language || 'it', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0,
        minimumFractionDigits: 0
      });
    } catch (error) {
      console.warn('[MobileSubscriptionPlans] Falling back to default currency formatter', error);
      return new Intl.NumberFormat('it', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0,
        minimumFractionDigits: 0
      });
    }
  }, [language]);

  const dateFormatter = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(language || 'it', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      console.warn('[MobileSubscriptionPlans] Falling back to default date formatter', error);
      return new Intl.DateTimeFormat('it', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    }
  }, [language]);

  const parseDate = useCallback((value) => {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === 'number') {
      const millis = value > 1e12 ? value : value * 1000;
      const date = new Date(millis);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return null;
      const date = new Date(trimmed);
      if (!Number.isNaN(date.getTime())) {
        return date;
      }
      const fallback = new Date(trimmed.replace(' ', 'T'));
      return Number.isNaN(fallback.getTime()) ? null : fallback;
    }

    return null;
  }, []);

  const formatDate = useCallback((value) => {
    const parsed = parseDate(value);
    if (!parsed) {
      return '--';
    }

    try {
      return dateFormatter.format(parsed);
    } catch (error) {
      console.warn('[MobileSubscriptionPlans] Failed to format date', { value, error });
      return parsed.toLocaleDateString();
    }
  }, [dateFormatter, parseDate]);

  const activePlanCode = (subscription?.plan_code || subscription?.planCode || '').toLowerCase();

  const activePlanConfig = useMemo(() => {
    if (!activePlanCode) {
      return PLAN_CONFIG[0];
    }

    return PLAN_CONFIG.find((plan) => {
      return Object.values(plan.prices || {}).some(
        (price) => price?.code && price.code.toLowerCase() === activePlanCode
      );
    }) || PLAN_CONFIG[0];
  }, [activePlanCode]);

  const alignBillingPeriodToPlan = useCallback(() => {
    if (billingAlignedRef.current || !activePlanCode) {
      return;
    }

    if (activePlanCode.includes('year')) {
      setBillingPeriod('yearly');
      billingAlignedRef.current = true;
    } else if (activePlanCode.includes('month')) {
      setBillingPeriod('monthly');
      billingAlignedRef.current = true;
    }
  }, [activePlanCode]);

  const loadSubscription = useCallback(async () => {
    setIsStatusLoading(true);
    try {
      const response = await get('/api/v1/user/ai-battery');
      const subscriptionData = response?.ai_battery?.subscription || response?.subscription || null;
      setSubscription(subscriptionData || null);
      billingAlignedRef.current = false;
    } catch (error) {
      if (error?.status === 401) {
        setSubscription(null);
        return;
      }

      const message = error?.message
        || t('mobilePlans.fetchError.desc', 'Impossibile leggere lo stato abbonamento');
      notify({
        variant: 'error',
        title: t('mobilePlans.fetchError.title', 'Errore nel recupero del piano'),
        description: message
      });
    } finally {
      setIsStatusLoading(false);
    }
  }, [get, notify, t]);

  useEffect(() => {
    if (!isActive) {
      return;
    }

    loadSubscription();
  }, [isActive, loadSubscription]);

  useEffect(() => {
    alignBillingPeriodToPlan();
  }, [alignBillingPeriodToPlan]);

  useEffect(() => {
    if (typeof registerSwipeBackGuard !== 'function') {
      return undefined;
    }

    const cleanup = registerSwipeBackGuard(() => true);

    return cleanup;
  }, [registerSwipeBackGuard]);

  useEffect(() => {
    if (!isActive || typeof window === 'undefined') {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const checkoutStatus = params.get('checkout');

    if (checkoutStatus === 'success') {
      notify({
        variant: 'success',
        title: t('mobilePlans.checkout.success.title', 'Pagamento completato'),
        description: t('mobilePlans.checkout.success.desc', 'Il piano e stato aggiornato, ricarico lo stato...')
      });
      trackEvent('subscription_checkout_success');
      loadSubscription();
    } else if (checkoutStatus === 'cancel') {
      notify({
        variant: 'warning',
        title: t('mobilePlans.checkout.cancel.title', 'Checkout annullato'),
        description: t('mobilePlans.checkout.cancel.desc', 'Nessun addebito. Puoi riprovare quando vuoi.')
      });
      trackEvent('subscription_checkout_cancel');
    } else {
      return;
    }

    params.delete('checkout');
    const newSearch = params.toString();
    const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}${window.location.hash || ''}`;
    window.history.replaceState({}, '', newUrl);
  }, [isActive, loadSubscription, notify, t]);

  const resolvePlanName = useCallback((code) => {
    if (!code) {
      return null;
    }
    const normalized = code.toLowerCase();
    for (const plan of PLAN_CONFIG) {
      const prices = Object.values(plan.prices || {});
      const match = prices.find((price) => price?.code && price.code.toLowerCase() === normalized);
      if (match) {
        return plan.name;
      }
    }
    return null;
  }, []);

  const handlePlanCheckout = useCallback(async (planCode) => {
    if (!planCode) {
      notify({
        variant: 'info',
        title: t('mobilePlans.actions.freeTitle', 'Piano Free'),
        description: t('mobilePlans.actions.freeDesc', 'Sei gia sul piano free. Per annullare un abbonamento usa il Billing Portal.')
      });
      trackEvent('subscription_checkout_free');
      return;
    }

    setProcessingAction({ type: 'checkout', planCode });
    trackEvent('subscription_checkout_start', { plan_code: planCode });

    try {
      const response = await post('/api/v1/payments/checkout-session', { plan_code: planCode });
      const redirectUrl = response?.url;

      if (redirectUrl) {
        notify({
          variant: 'info',
          title: t('mobilePlans.checkout.redirect.title', 'Reindirizzamento a Stripe'),
          description: t('mobilePlans.checkout.redirect.desc', 'Ti portiamo al checkout per completare il pagamento.')
        });
        window.location.href = redirectUrl;
        return;
      }

      notify({
        variant: 'error',
        title: t('mobilePlans.checkout.missingUrl.title', 'Checkout non disponibile'),
        description: t('mobilePlans.checkout.missingUrl.desc', 'Non ho ricevuto il link di pagamento. Riprova piu tardi.')
      });
    } catch (error) {
      const message = error?.errorData?.message || error?.message || 'Errore inatteso';
      notify({
        variant: 'error',
        title: t('mobilePlans.checkout.error.title', 'Errore durante il checkout'),
        description: message
      });
    } finally {
      setProcessingAction(null);
    }
  }, [notify, post, t]);

  const buildReturnUrls = useCallback(() => {
    if (typeof window === 'undefined') {
      return {};
    }

    try {
      const successUrl = new URL(window.location.href);
      successUrl.searchParams.set('checkout', 'success');

      const cancelUrl = new URL(window.location.href);
      cancelUrl.searchParams.set('checkout', 'cancel');

      return {
        success: successUrl.toString(),
        cancel: cancelUrl.toString()
      };
    } catch (error) {
      console.warn('[MobileSubscriptionPlans] Failed to build return URLs', error);
      return {};
    }
  }, []);

  const handleChangePlanNow = useCallback(async (planCode) => {
    if (!planCode) {
      return;
    }

    setProcessingAction({ type: 'now', planCode });
    trackEvent('subscription_change_now', { plan_code: planCode });
    try {
      const { success, cancel } = buildReturnUrls();
      const payload = { plan_code: planCode };
      if (success) {
        payload.success_url = success;
      }
      if (cancel) {
        payload.cancel_url = cancel;
      }

      const response = await post('/api/v1/payments/change-plan-now', payload);
      const paymentRequired = response?.payment_required ?? response?.paymentRequired ?? false;
      const redirectUrl = response?.url;

      if (paymentRequired) {
        if (redirectUrl) {
          notify({
            variant: 'info',
            title: t('mobilePlans.changeNow.redirect.title', 'Pagamento richiesto'),
            description: t('mobilePlans.changeNow.redirect.desc', 'Ti portiamo a Stripe per saldare la differenza.')
          });
          window.location.href = redirectUrl;
          return;
        }

        notify({
          variant: 'error',
          title: t('mobilePlans.changeNow.redirectError.title', 'Checkout non disponibile'),
          description: t('mobilePlans.changeNow.redirectError.desc', 'Non ho ricevuto il link di pagamento. Riprova piu tardi.')
        });
        return;
      }

      notify({
        variant: 'success',
        title: t('mobilePlans.changeNow.success.title', 'Piano aggiornato subito'),
        description: t('mobilePlans.changeNow.success.desc', 'Il credito residuo e stato proratato. Rileggo lo stato...')
      });
      await loadSubscription();
    } catch (error) {
      const message = error?.errorData?.message || error?.message || 'Errore inatteso';
      notify({
        variant: 'error',
        title: t('mobilePlans.changeNow.error.title', 'Cambio immediato non riuscito'),
        description: message
      });
    } finally {
      setProcessingAction(null);
    }
  }, [buildReturnUrls, loadSubscription, notify, post, t]);

  const handleSchedulePlanChange = useCallback(async (planCode) => {
    if (!planCode) {
      return;
    }

    setProcessingAction({ type: 'schedule', planCode });
    trackEvent('subscription_change_schedule', { plan_code: planCode });
    try {
      await post('/api/v1/payments/schedule-plan-change', { plan_code: planCode });
      notify({
        variant: 'success',
        title: t('mobilePlans.schedule.success.title', 'Cambio programmato'),
        description: t('mobilePlans.schedule.success.desc', 'Il nuovo piano partira al prossimo rinnovo.')
      });
      await loadSubscription();
    } catch (error) {
      const message = error?.errorData?.message || error?.message || 'Errore inatteso';
      notify({
        variant: 'error',
        title: t('mobilePlans.schedule.error.title', 'Programmazione non riuscita'),
        description: message
      });
    } finally {
      setProcessingAction(null);
    }
  }, [loadSubscription, notify, post, t]);

  const handleOpenBillingPortal = useCallback(async () => {
    setIsPortalLoading(true);
    trackEvent('subscription_portal_open');
    try {
      const returnUrl = typeof window !== 'undefined' ? window.location.href : undefined;
      const payload = returnUrl ? { return_url: returnUrl } : {};
      const response = await post('/api/v1/payments/billing-portal', payload);
      const redirectUrl = response?.url;

      if (redirectUrl) {
        window.location.href = redirectUrl;
        return;
      }

      notify({
        variant: 'error',
        title: t('mobilePlans.portal.missingUrl.title', 'Billing portal non disponibile'),
        description: t('mobilePlans.portal.missingUrl.desc', 'Nessun link ricevuto. Controlla piu tardi.')
      });
    } catch (error) {
      const message = error?.errorData?.message || error?.message || 'Errore inatteso';
      notify({
        variant: 'error',
        title: t('mobilePlans.portal.error.title', 'Errore di billing'),
        description: message
      });
    } finally {
      setIsPortalLoading(false);
    }
  }, [notify, post, t]);

  const statusKey = (subscription?.status || 'inactive').toLowerCase();
  const statusLabel = STATUS_LABELS[statusKey] || STATUS_LABELS.inactive;
  const statusVariant = (statusKey === 'active' || statusKey === 'trialing' || statusKey === 'grace')
    ? 'positive'
    : (statusKey === 'past_due' || statusKey === 'unpaid' ? 'warning' : 'muted');

  const nextRefresh = subscription?.next_refresh_at
    || subscription?.current_period_end
    || subscription?.period_end
    || null;

  const pendingPlanCode = (subscription?.pending_plan_code || subscription?.pendingPlanCode || '').toLowerCase();
  const currentPlanLabel = useMemo(() => {
    if (!subscription) {
      return 'Free';
    }
    const code = (subscription.plan_code || subscription.planCode || '').toLowerCase();
    const name = subscription.plan_name || subscription.planName;
    if (name) {
      return `${name} ${code ? `(${code})` : ''}`.trim();
    }
    if (code) {
      return resolvePlanName(code) || code;
    }
    return 'Free';
  }, [resolvePlanName, subscription]);

  const renderPlanCard = (plan) => {
    const selectedPrice = plan.prices[billingPeriod] || plan.prices.monthly;
    const targetCode = selectedPrice?.code || null;
    const isSamePlan = targetCode && targetCode.toLowerCase() === activePlanCode;
    const isProcessing = processingAction?.planCode
      ? processingAction.planCode === targetCode
      : false;
    const hasPendingChange = pendingPlanCode && targetCode
      ? pendingPlanCode === targetCode.toLowerCase()
      : false;
    const isActionDisabled = isProcessing || !targetCode || isSamePlan;
    const isScheduleDisabled = isActionDisabled || hasPendingChange;

    const periodLabel = billingPeriod === 'yearly'
      ? t('mobilePlans.period.year', 'anno')
      : t('mobilePlans.period.month', 'mese');

    const priceLabel = selectedPrice?.amount
      ? currencyFormatter.format(selectedPrice.amount)
      : (selectedPrice?.label || '--');

    return (
      <div key={plan.id} className={`mobile-plan-card ${plan.className || ''} ${plan.highlight ? 'is-highlighted' : ''} ${isSamePlan ? 'is-current' : ''}`}>
        <div className="mobile-plan-card__header">
          <div className="mobile-plan-card__title-row">
            <div className="mobile-plan-chip">
              <img src={iconAi} alt="AI" />
              <span>{plan.name}</span>
            </div>
            {plan.badge && (
              <span className="mobile-plan-badge">{plan.badge}</span>
            )}
          </div>
          <p className="mobile-plan-description">{plan.description}</p>
        </div>

        <div className="mobile-plan-price-row">
          <div>
            <div className="mobile-plan-price">
              {priceLabel}
              <span className="mobile-plan-period">/{periodLabel}</span>
            </div>
            {selectedPrice?.note && (
              <div className="mobile-plan-note">{selectedPrice.note}</div>
            )}
          </div>
          {isSamePlan && (
            <span className="mobile-plan-pill">
              {t('mobilePlans.currentPlan', 'Piano attivo')}
            </span>
          )}
          {hasPendingChange && (
            <span className="mobile-plan-pill ghost">
              {t('mobilePlans.pendingPlan', 'Cambio programmato')}
            </span>
          )}
        </div>

        <ul className="mobile-plan-features">
          {plan.features.map((feature) => (
            <li key={feature}>
              <CheckIcon size={14} />
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        <div className="mobile-plan-actions">
          <button
            type="button"
            className="mobile-plan-cta"
            onClick={() => handleChangePlanNow(targetCode)}
            disabled={isActionDisabled}
            data-variant={plan.highlight ? 'primary' : 'secondary'}
          >
            {isProcessing && processingAction?.type === 'now'
              ? t('mobilePlans.actions.processing', 'Attendi...')
              : t('mobilePlans.actions.changeNow', 'Subito (saldo proporzionale)')}
          </button>
          <button
            type="button"
            className="mobile-plan-ghost"
            onClick={() => handleSchedulePlanChange(targetCode)}
            disabled={isScheduleDisabled}
          >
            {isProcessing && processingAction?.type === 'schedule'
              ? t('mobilePlans.actions.processing', 'Attendi...')
              : t('mobilePlans.actions.schedule', 'Dal prossimo rinnovo')}
          </button>
          <button
            type="button"
            className="mobile-plan-link"
            onClick={() => handlePlanCheckout(targetCode)}
            disabled={isProcessing || isPortalLoading || !targetCode || isSamePlan}
          >
            {t('mobilePlans.actions.checkout', 'Checkout immediato')}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="mobile-plans-page">
      <div className="mobile-plans-content">
        <div className="mobile-plans-hero">
          <div className="mobile-plans-hero-header">
            <div className="mobile-plans-heading">
              <h1 className="mobile-plans-title">
                {t('mobilePlans.title', 'Scegli il tuo piano')}
              </h1>
              <p className="mobile-plans-subtitle">
                {t('mobilePlans.subtitle', 'Potenzia la batteria AI e gestisci i pagamenti con Stripe.')}
              </p>
            </div>
          </div>

          <div className="mobile-plans-status-cards">
            <div className="mobile-plan-info-card">
              <div className="mobile-plan-info-header">
                <div className="mobile-plan-info-copy">
                  <span className="mobile-plan-info-label">
                    {t('mobilePlans.current', 'Piano')}
                  </span>
                  <span className="mobile-plan-info-value">{currentPlanLabel}</span>
                </div>
                <span className={`mobile-plans-status-pill status-${statusVariant}`}>
                  {statusLabel}
                </span>
              </div>
              {pendingPlanCode && (
                <div className="mobile-plan-info-note">
                  {t('mobilePlans.pendingPlan.hero', 'Cambio programmato al rinnovo')}: {resolvePlanName(pendingPlanCode) || pendingPlanCode}
                </div>
              )}
            </div>

            <div className="mobile-plan-info-card">
              <div className="mobile-plan-info-row">
                <span className="mobile-plan-info-label">
                  {t('mobilePlans.nextRefresh', 'Prossimo rinnovo')}
                </span>
                <span className="mobile-plan-info-value">{formatDate(nextRefresh)}</span>
              </div>
            </div>
          </div>

          <div className="mobile-plans-actions">
            <button
              type="button"
              className="mobile-plans-portal"
              onClick={handleOpenBillingPortal}
              disabled={isPortalLoading}
            >
              <img src={iconBilling} alt="" />
              <span>
                {isPortalLoading
                  ? t('mobilePlans.portal.loading', 'Apertura Billing Portal...')
                  : t('mobilePlans.portal.cta', 'Gestisci pagamento')}
              </span>
            </button>
          </div>
        </div>

        <div className="mobile-plans-toggle">
          <div className="mobile-plans-toggle-control">
            <button
              type="button"
              className={`mobile-plans-toggle-option ${billingPeriod === 'monthly' ? 'is-active' : ''}`}
              onClick={() => setBillingPeriod('monthly')}
            >
              {t('mobilePlans.period.monthly', 'Mensile')}
            </button>
            <button
              type="button"
              className={`mobile-plans-toggle-option ${billingPeriod === 'yearly' ? 'is-active' : ''}`}
              onClick={() => setBillingPeriod('yearly')}
            >
              {t('mobilePlans.period.yearly', 'Annuale')}
              <span className="mobile-plans-toggle-save">
                {t('mobilePlans.period.save', 'Risparmia')}
              </span>
            </button>
          </div>
        </div>

        {isStatusLoading ? (
          <div className="mobile-plans-skeleton">
            <Skeleton width="80%" height={28} shimmer />
            <Skeleton width="60%" height={18} shimmer />
            <Skeleton width="100%" height={140} shimmer variant="rounded" />
          </div>
        ) : (
          <div className="mobile-plans-grid">
            {PLAN_CONFIG.map((plan) => renderPlanCard(plan))}
          </div>
        )}

        <div className="mobile-plans-footnote">
          <p>
            {t('mobilePlans.footnote', 'Il checkout avviene su Stripe in modo sicuro. Dopo il pagamento tornerai qui con lo stato aggiornato.')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default MobileSubscriptionPlans;
